import { useState } from 'react';
import type { ShareRow, ShareState } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Modal } from '#client/components/ui/Modal';
import { useMutation } from '#client/hooks/useMutation';
import { sendShare } from './share.service';

import { formatDateTime, joinList } from '#client/lib/format';
import { useToast } from '#client/providers/ToastProvider';

interface ShareWhatsAppModalProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
  state: ShareState;
  /** The most recent successful send of anything, for the resend warning. */
  lastSent: ShareRow | undefined;
  onSent: () => void;
}

/**
 * The last moment anybody can catch a misdelivery.
 *
 * A report leaving the building is not undoable — WhatsApp gives businesses no
 * way to recall a message — so this dialog is deliberately unhurried about the
 * two things that would make it wrong: **which number** it is going to, shown in
 * full and never truncated, and **which tests** are included.
 */
export function ShareWhatsAppModal({
  open,
  onClose,
  patientId,
  patientName,
  state,
  lastSent,
  onSent,
}: ShareWhatsAppModalProps) {
  const toast = useToast();
  const share = useMutation('That send did not go through.');
  // A send the server recorded but WhatsApp refused: not a request failure.
  const [refusal, setRefusal] = useState<string | null>(null);
  const sending = share.isPending;
  const failure = refusal ?? share.errorMessage;

  const resend = lastSent !== undefined;

  async function handleSend() {
    setRefusal(null);
    const outcome = await share.run(() =>
      sendShare(patientId, {
        reportIds: state.included.map((report) => report.id),
        // The button already says "Send again" when this is set.
        confirmResend: resend,
      }),
    );
    if (!outcome.ok) return;

    onSent();

    // A 201 means the *attempt* was recorded, not that it went out. A failed
    // row keeps the dialog open with its own sentence rather than showing a
    // success toast for a message nobody received.
    if (outcome.value.status === 'failed') {
      setRefusal(outcome.value.error_message ?? 'WhatsApp refused the message.');
      return;
    }

    toast.success(`Report sent to ${state.recipient ?? 'the patient'}`);
    onClose();
  }

  return (
    <Modal
      open={open}
      title="Send report on WhatsApp"
      onClose={sending ? () => undefined : onClose}
      primaryAction={{
        label: resend ? 'Send again' : 'Send',
        onAction: () => void handleSend(),
        loading: sending,
      }}
      secondaryAction={sending ? undefined : { label: 'Cancel', onAction: onClose }}
    >
      <div className="c-form-stack">
        {failure && <Banner tone="critical">{failure}</Banner>}

        <ShareDetails state={state} patientName={patientName} lastSent={lastSent} />
      </div>
    </Modal>
  );
}

interface ShareDetailsProps {
  state: ShareState;
  patientName: string;
  lastSent: ShareRow | undefined;
}

function ShareDetails({ state, patientName, lastSent }: ShareDetailsProps) {
  return (
    <>
      {/* Shown in full and deliberately not abbreviated. A misdelivery is a
        clinical disclosure, and this is the last place it can be caught. */}
      <dl className="c-summary">
        <div className="c-summary__row">
          <dt>To</dt>
          <dd className="tnum">{state.recipient}</dd>
        </div>
        <div className="c-summary__row">
          <dt>Patient</dt>
          <dd>{patientName}</dd>
        </div>
        <div className="c-summary__row">
          <dt>Attached</dt>
          <dd>1 PDF · {joinList(state.included.map((report) => report.name))}</dd>
        </div>
      </dl>

      {state.excluded.length > 0 && (
        // Non-dismissible. The operator is about to tell a patient their
        // results are ready; they need to know which ones are not in the
        // envelope before, not after.
        <Banner tone="warning" title="Not everything is finished">
          Only the {state.included.length} finalized{' '}
          {state.included.length === 1 ? 'test is' : 'tests are'} included.{' '}
          {joinList(state.excluded.map((report) => report.name))}{' '}
          {state.excluded.length === 1 ? 'is' : 'are'} still on the bench.
        </Banner>
      )}

      {lastSent && (
        <Banner tone="info">
          Already sent to {lastSent.to_e164} on {formatDateTime(lastSent.created_at)} by{' '}
          {lastSent.shared_by.first_name}. Sending again delivers another copy.
        </Banner>
      )}

      {state.consent_at && (
        <p className="c-field__help">
          Consent recorded {formatDateTime(state.consent_at)}. WhatsApp is not a secure medical
          channel — anyone holding the phone can open the report.
        </p>
      )}
    </>
  );
}
